import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the types of data that can be refreshed
export type RefreshDataType = 'categories' | 'banners' | 'navbar' | 'footer' | 'products';

// Refresh trigger state: tracks timestamp for each data type
interface RefreshTrigger {
  categories: number;
  banners: number;
  navbar: number;
  footer: number;
  products: number;
}

// Context value interface
interface DataRefreshContextValue {
  refreshTrigger: RefreshTrigger;
  triggerRefresh: (type: RefreshDataType) => void;
}

// Create the context
const DataRefreshContext = createContext<DataRefreshContextValue | undefined>(undefined);

// Provider component
export const DataRefreshProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState<RefreshTrigger>({
    categories: 0,
    banners: 0,
    navbar: 0,
    footer: 0,
    products: 0,
  });

  // Function to trigger refresh for a specific data type
  const triggerRefresh = (type: RefreshDataType) => {
    setRefreshTrigger((prev) => ({
      ...prev,
      [type]: Date.now(), // Update timestamp to trigger re-renders
    }));
    console.log(`🔄 Data refresh triggered for: ${type}`);
  };

  return (
    <DataRefreshContext.Provider value={{ refreshTrigger, triggerRefresh }}>
      {children}
    </DataRefreshContext.Provider>
  );
};

// Custom hook to use the refresh context
export const useDataRefresh = (): DataRefreshContextValue => {
  const context = useContext(DataRefreshContext);
  if (!context) {
    throw new Error('useDataRefresh must be used within a DataRefreshProvider');
  }
  return context;
};
